import React, { useEffect, useState, useMemo, useContext } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../assets/styles/Dashboard.css";
import api from "../../services/api";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { userRoles } from "../../App";
import FullPageLoader from "../../components/ui/fullPageLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeChart, setActiveChart] = useState("points");

  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/users/student-dashboard");
        const sanitizedData = {
          ...res.data,
          calendar: res.data?.calendar?.map((event) => ({
            ...event,
            endDate: event.endtDate || event.endDate,
          })),
        };
        setData(sanitizedData);
        setLoading(false);
      } catch (err) {
        console.error("Error loading dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const eventsByDate = useMemo(() => {
    const map = {};
    if (!data?.calendar) return map;
    data?.calendar?.forEach((e) => {
      const d = new Date(e.startDate);
      if (isNaN(d.getTime())) return;
      const key = d.toDateString();
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [data]);

  const handleDayClick = (date) => {
    const key = date.toDateString();
    const events = eventsByDate[key] || [];
    if (events.length > 0) {
      setSelectedDate(date);
      setDayEvents(events);
      setShowModal(true);
    }
  };

  // --- Charts Config ---
  const pointsChartData = {
    labels: ["Academic", "Sports", "Social", "Compete"],
    datasets: [
      {
        label: "Points",
        data: [
          data?.academicPoints || 0,
          data?.sportsPoints || 0,
          data?.entertainmentPoints || 0,
          data?.competitionPoints || 0,
        ],
        backgroundColor: ["#4f46e5", "#10b981", "#ec4899", "#f59e0b"],
        borderRadius: 20,
        barThickness: 32,
        borderSkipped: false,
      },
    ],
  };

  const pointsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true,
        grid: { borderDash: [8, 8], color: "#f1f5f9", drawBorder: false },
        ticks: { font: { family: "Outfit" } },
      },
      x: { grid: { display: false }, ticks: { font: { family: "Outfit" } } },
    },
    layout: { padding: { top: 10, bottom: 10 } },
  };

  const activitiesChartData = {
    labels: ["Done", "Active", "Cancelled"],
    datasets: [
      {
        data: [data?.FINISHED || 0, data?.ACTIVE || 0, data?.CANCELLED || 0],
        backgroundColor: ["#10b981", "#3b82f6", "#ef4444"],
        borderWidth: 0,
        hoverOffset: 15,
      },
    ],
  };

  if (loading) {
    return <FullPageLoader loadingName="Loading user Dashboard..." />;
  }
  if (!data)
    return <div className="error-screen">Failed to load dashboard data.</div>;

  return (
    <div
      style={{ marginTop: "80px" }}
      className="container-fluid dashboard-wrapper pb-5"
    >
      {/* 1. Premium Header */}
      <div className="row mb-4 mb-lg-5">
        <div className="col-12">
          <div className="premium-header p-4 p-lg-5 d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3 position-relative overflow-hidden">
            <div className="position-relative z-2">
              <span className="text-white-50 fw-medium text-uppercase ls-1 small mb-1 d-block">
                {getGreeting()},
              </span>
              <h1 className="display-5 fw-bold text-white m-0">
                {user?.fullName.split(" ")[0]}
              </h1>
              <p className="text-white-50 m-0 mt-2">
                You have{" "}
                <strong className="text-white">
                  {
                    data.calendar?.filter(
                      (c) => c.type === "Booking" && c.status === "upcoming",
                    ).length
                  }{" "}
                  upcoming
                </strong>{" "}
                events.
              </p>
            </div>

            <div className="d-flex align-items-center gap-3 position-relative z-2">
              {user?.role === userRoles.supervisor && (
                <button
                  onClick={() => navigate("/supervisor-dashboard")}
                  className="btn-glass-outline"
                >
                  <i className="bi bi-shield-lock me-2"></i>Supervisor
                </button>
              )}
              <div className="score-float">
                <div className="score-icon">
                  <i className="bi bi-trophy-fill"></i>
                </div>
                <div className="d-flex flex-column">
                  <span
                    className="small text-uppercase fw-bold opacity-75"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Total Score
                  </span>
                  <span className="h4 fw-bold m-0">{data.totalPoints}</span>
                </div>
              </div>
            </div>
            <div className="circle-shape shape-1"></div>
            <div className="circle-shape shape-2"></div>
          </div>
        </div>
      </div>

      {/* 2. Compact Stats Row (Edited for Mobile) */}
      <div className="row g-2 g-md-4 mb-4">
        <div className="col-12 col-md-3">
          <StatCard
            title="Active"
            value={data.ACTIVE}
            icon="bi-lightning-charge-fill"
            color="blue"
          />
        </div>

        <div className="col-12 col-md-3">
          <StatCard
            title="Finished"
            value={data.FINISHED}
            icon="bi-patch-check-fill"
            color="green"
          />
        </div>

        <div className="col-12 col-md-3">
          <StatCard
            title="Upcoming"
            value={data.calendar?.filter((c) => c.type === "Booking").length}
            icon="bi-calendar-week-fill"
            color="orange"
          />
        </div>

        <div className="col-12 col-md-3">
          <StatCard
            title="Attendance"
            value={data.attendanceRate}
            icon="bi-pie-chart-fill"
            color="purple"
            isPercent
          />
        </div>
      </div>

      {/* 3. Main Grid */}
      <div className="row g-4">
        {/* Charts */}
        <div className="col-lg-8 order-2 order-lg-1">
          <div className="premium-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-bold m-0 bg-primary-subtle rounded-5 p-2 text-primary">
                  <i className="bi-activity"></i>
                </h5>
              </div>
              <div className="tab-switcher p-1 rounded-pill bg-light d-flex">
                <button
                  className={`tab-btn rounded-pill px-4 py-2 small fw-bold border-0 ${activeChart === "points" ? "bg-white shadow-sm text-primary" : "bg-transparent text-muted"}`}
                  onClick={() => setActiveChart("points")}
                >
                  Points
                </button>
                <button
                  className={`tab-btn rounded-pill px-4 py-2 small fw-bold border-0 ${activeChart === "status" ? "bg-white shadow-sm text-primary" : "bg-transparent text-muted"}`}
                  onClick={() => setActiveChart("status")}
                >
                  Activity
                </button>
              </div>
            </div>

            <div
              className="chart-canvas position-relative"
              style={{ height: "360px" }}
            >
              {activeChart === "points" ? (
                <Bar data={pointsChartData} options={pointsChartOptions} />
              ) : (
                <div className="h-100 w-100 d-flex justify-content-center align-items-center position-relative">
                  <div style={{ width: "240px", height: "240px" }}>
                    <Doughnut
                      data={activitiesChartData}
                      options={{
                        cutout: "80%",
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                      }}
                    />
                  </div>
                  <div
                    className="position-absolute text-center"
                    style={{ pointerEvents: "none" }}
                  >
                    <h2 className="fw-bold m-0 text-dark display-6">
                      {data.totalActivities || data.ACTIVE + data.FINISHED}
                    </h2>
                    <small className="text-muted fw-bold text-uppercase ls-1">
                      Total
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="col-lg-4 order-1 order-lg-2">
          <div className="premium-card p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold m-0 text-dark">Calendar</h5>
              <span className="text-primary fw-bold small bg-primary-subtle px-3 py-1 rounded-pill">
                {new Date().toLocaleString("default", { month: "long" })}
              </span>
            </div>
            <div className="calendar-styled-wrapper">
              <Calendar
                onClickDay={handleDayClick}
                locale="en-US"
                tileContent={({ date }) =>
                  eventsByDate[date.toDateString()] ? (
                    <div className="event-dot"></div>
                  ) : null
                }
                prevLabel={<i className="bi bi-chevron-left small"></i>}
                nextLabel={<i className="bi bi-chevron-right small"></i>}
                className="border-0 w-100 font-outfit"
              />
            </div>
          </div>

          <div className="premium-card p-4">
            <h6 className="fw-bold text-muted small text-uppercase ls-1 mb-3">
              Next Up
            </h6>
            <div className="d-flex flex-column gap-3">
              {data?.calendar
                .filter((e) => e.status === "upcoming")
                .slice(0, 3)
                .map((evt, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center gap-3 p-2 rounded-3 hover-effect"
                  >
                    <div
                      className={`date-stack ${evt.type === "Activity" ? "bg-primary-subtle text-primary" : "bg-warning-subtle text-warning"}`}
                    >
                      <span className="fw-bold h5 m-0">
                        {new Date(evt.startDate).getDate()}
                      </span>
                      <span
                        className="small fw-bold text-uppercase"
                        style={{ fontSize: "0.6rem" }}
                      >
                        {new Date(evt.startDate).toLocaleString("default", {
                          month: "short",
                        })}
                      </span>
                    </div>
                    <div className="overflow-hidden">
                      <h6 className="fw-bold text-dark m-0 text-truncate">
                        {evt.event}
                      </h6>
                      <span className="small text-muted">
                        {evt.type !== "Activity"
                          ? evt.slot
                          : new Date(evt.startDate).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                      </span>
                    </div>
                  </div>
                ))}
              {(!data?.calendar ||
                data?.calendar.filter((e) => e.status === "upcoming").length ===
                  0) && (
                <div className="text-center text-muted small py-2">
                  No upcoming events
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Mobile Sheet Modal (Fixed & Premium) */}
      {showModal && (
        <div
          className="sheet-backdrop-blur"
          onClick={() => setShowModal(false)}
        >
          <div
            className="sheet-modal-premium"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-handle-pill"></div>

            <div className="sheet-header-clean p-4 d-flex justify-content-between align-items-end border-bottom">
              <div className="d-flex gap-3 align-items-center">
                <div className="date-big-box bg-primary text-white shadow-lg">
                  <span className="display-5 fw-bold lh-1">
                    {selectedDate?.getDate()}
                  </span>
                  <span className="small fw-bold text-white-50 text-uppercase">
                    {selectedDate?.toLocaleDateString("en-US", {
                      weekday: "short",
                    })}
                  </span>
                </div>
                <div>
                  <h4 className="fw-bold m-0 text-dark">Agenda</h4>
                  <span className="text-muted small">
                    {selectedDate?.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
              <button
                className="btn-close-clean"
                onClick={() => setShowModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="sheet-body-scroll p-4">
              {dayEvents.length > 0 ? (
                <div className="timeline-master">
                  {dayEvents.map((evt, idx) => (
                    <div key={idx} className="tl-row mb-4">
                      <div className="tl-time-col">
                        <span className="fw-bold text-dark small d-block">
                          {evt.type !== "Activity"
                            ? evt.slot.split(" ")[0]
                            : new Date(evt.startDate).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                        </span>
                      </div>

                      <div className="tl-line-col">
                        <div className="tl-node"></div>
                        <div className="tl-path"></div>
                      </div>

                      <div
                        className={`tl-card shadow-sm p-3 rounded-4 flex-grow-1 border-start border-4 ${getStatusBorder(evt.status)}`}
                      >
                        <div className="d-flex justify-content-between mb-2">
                          <span className="custom-tag bg-white text-dark shadow-sm">
                            {evt.type}
                          </span>
                          <span
                            className={`custom-status text-${getStatusColor(evt.status)}`}
                          >
                            {evt.status}
                          </span>
                        </div>
                        <h6 className="fw-bold text-dark m-0">{evt.event}</h6>
                        <div className="d-flex align-items-center gap-2 mt-2 text-muted small">
                          <i className="bi bi-geo-alt-fill"></i> Campus Center
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5 opacity-50">
                  <i className="bi bi-calendar2-week display-1"></i>
                  <p className="mt-3">Relax, no plans for today.</p>
                </div>
              )}
            </div>

            <div className="p-3 bg-white border-top">
              <button
                className="btn btn-dark w-100 rounded-pill py-3 fw-bold shadow-lg"
                onClick={() => setShowModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Compact Stat Card (Auto-Adjusts for Mobile) ---
const StatCard = ({ title, value, icon, color }) => (
  <div className="premium-card p-1 p-md-3 h-100 d-flex align-items-center gap-1 gap-md-1 hover-up stat-card-compact">
    <div className={`icon-box-soft bg-${color}-subtle text-${color}`}>
      <i className={`bi ${icon}`}></i>
    </div>
    <div>
      <h3 className="fw-bold m-0 text-dark stat-val">{value || 0}</h3>
      <span className="text-muted fw-bold text-uppercase stat-label">
        {title}
      </span>
    </div>
  </div>
);

const getStatusBorder = (status) => {
  if (status === "upcoming") return "border-primary";
  if (status === "finished") return "border-success";
  if (status === "cancelled") return "border-danger";
  return "border-secondary";
};

const getStatusColor = (status) => {
  if (status === "upcoming") return "primary";
  if (status === "finished") return "success";
  if (status === "cancelled") return "danger";
  return "secondary";
};

export default Dashboard;
